import { useState, useEffect, useCallback, useRef } from 'react';
import { getSummaryHotEventAvgSentiments } from '@op/services';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';

const buildKey = (filter: any) => `${filter?.start_time}_${filter?.end_time}`;

/**
 * Hot Event Avg Sentiment 情感分组列表 Hook
 *
 * 调用 avg_sentiments 接口，返回当前筛选条件下真实存在的情感分组。
 * 用于动态控制 sentiment Segment 中 positive / negative / neutral 的显示。
 *
 * - 暴露 reset() 供外部在修改 filter/sentimentValue 时同步调用
 * - 内部 useEffect 兜底 onFilterDateSync 等通过 Redux dispatch 触发的 filter 变化场景
 */
export const useAvgSentiments = (gameInfo: any, filter: any) => {
  const [avgSentiments, setAvgSentiments] = useState<string[]>([]);
  const { loading: avgSentimentsLoading, wrapperCancelRequest } =
    useCancelRequestHook();

  const prevKeyRef = useRef<string>('');

  const fetchAvgSentiments = useCallback(
    (overrideFilter?: any) => {
      setAvgSentiments([]);
      const effectiveFilter = overrideFilter || filter;

      if (!gameInfo?.edition_unified_id || !effectiveFilter) return;

      const params = {
        ...gameInfo,
        channel_type: 'total',
        filter_keyword_response: true,
        ...(effectiveFilter || {}),
      };

      const currentKey = buildKey(effectiveFilter);
      if (currentKey === prevKeyRef.current) return;
      prevKeyRef.current = currentKey;

      wrapperCancelRequest(getSummaryHotEventAvgSentiments, params).then(
        (res: any) => {
          const resData = res?.data || res || {};
          const sentiments: string[] = resData.avg_sentiments || [];
          setAvgSentiments(sentiments);
        },
      );
    },
    [gameInfo, filter, wrapperCancelRequest],
  );

  /**
   * 重新加载情感分组列表。
   * 外部在 setFilter / setSentimentValue 的同事件处理器中调用，
   * React 18 自动批处理，只触发一次渲染。
   */
  const reset = useCallback(
    (overrideFilter?: any) => {
      fetchAvgSentiments(overrideFilter);
    },
    [fetchAvgSentiments],
  );

  // filter 变化时自动加载（首次 + 后续）
  useEffect(() => {
    if (!filter) return;
    reset(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  return {
    avgSentiments,
    avgSentimentsLoading,
    reset,
  };
};
